import { useEffect, useRef, useState, useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col, Badge, Alert, Spinner } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"

import ThemeModal from "components/Common/ThemeModal"
import InputField from "components/Common/InputField"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import SearchableSelect from "components/Common/SearchableSelect"
import ConfirmModal from "components/Common/ConfirmModal"

import { useCreateWorkOrder, useEditWorkOrder } from "hooks/useWorkOrder"
import { getAccountVendors } from "api/accountVendorApi"
import { getMoneyReceipts } from "api/moneyReceiptApi"
import { getServiceLists } from "api/serviceListApi"
import { getEmployees } from "api/employeeApi"
import { getWorkOrderByVehicle } from "api/workOrderApi"
import { buildVehicleOptionLabel } from "helpers/stock_helpers"

const emptyItem = { service_list_id: "", employee_id: "", vendor_id: "", status: "pending", due_date: "", notes: "" }

const pad2 = n => String(n).padStart(2, "0")

const toLocalDatetimeString = date =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`

const nowLocalDatetimeString = () => toLocalDatetimeString(new Date())

// "Due date" autofill: one day after the assigned date, same time of day.
const addOneDay = datetimeLocal => {
  if (!datetimeLocal) return ""
  const date = new Date(datetimeLocal)
  if (Number.isNaN(date.getTime())) return ""
  date.setDate(date.getDate() + 1)
  return toLocalDatetimeString(date)
}

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
]

const computeStatusFromItems = items => {
  if (!items?.length) return "pending"
  const allCompleted = items.every(i => i.status === "completed")
  if (allCompleted) return "completed"
  const hasInProgress = items.some(i => i.status === "in_progress")
  if (hasInProgress) return "in_progress"
  return "pending"
}

const STATUS_BADGE = {
  pending: "secondary",
  in_progress: "info",
  completed: "success",
  cancelled: "danger",
}

const formatDueDateLabel = value => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
}

const buildItemSummary = item => {
  const parts = [
    item.service_list_id?.label,
    item.employee_id?.label,
    item.vendor_id?.label,
    STATUS_OPTIONS.find(s => s.value === item.status)?.label,
  ].filter(Boolean)
  const due = formatDueDateLabel(item.due_date)
  if (due) parts.push(`Due: ${due}`)
  return parts.join(" — ")
}

const emptyVehicleInfo = () => ({
  chassis_no: "",
  engine_no: "",
  brand: "",
  model: "",
  model_year: "",
  color: "",
  cc: "",
  fuel_type: "",
  tyre_size: "",
  mileage: "",
})

const buildVehicleInfoFromRaw = raw => {
  if (!raw) return emptyVehicleInfo()
  return {
    ...emptyVehicleInfo(),
    chassis_no: raw.full_chassis || "",
    engine_no: raw.full_engine || "",
    brand: raw.brand?.name || "",
    model: raw.model?.name || "",
    model_year: raw.year?.name || "",
    color: raw.exteriorColor?.name || raw.color || "",
    cc: raw.engineCC?.name || raw.engineCc?.name || "",
    fuel_type: raw.engineType?.name || raw.fuelType?.name || "",
    tyre_size: raw.tire_size || "",
    mileage: raw.mileage ?? "",
  }
}

const WorkOrderAddEdit = ({ modal, toggleModal, isEdit, editedData, onSuccess }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateWorkOrder()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditWorkOrder()

  const [showVehicleDetails, setShowVehicleDetails] = useState(false)
  const [matchedWorkOrder, setMatchedWorkOrder] = useState(null)
  const [checkingVehicle, setCheckingVehicle] = useState(false)

  // A pending match is loaded into the form for editing; anything else
  // (in_progress/completed/cancelled) is shown read-only — only one active
  // work order is expected per vehicle.
  const isPreviewOnly = !isEdit && !!matchedWorkOrder && matchedWorkOrder.status !== "pending"

  const validation = Yup.object({
    vehicle_id: Yup.mixed().required("Vehicle is required"),
    money_receipt_id: Yup.mixed().nullable(),
    assigned_date: Yup.string().required("Assigned date is required"),
    notes: Yup.string().nullable(),
    items: Yup.array()
      .of(
        Yup.object({
          service_list_id: Yup.mixed().required("Service is required"),
          employee_id: Yup.mixed().required("Employee is required"),
          vendor_id: Yup.mixed().required("Vendor is required"),
          status: Yup.string().required("Status is required"),
          due_date: Yup.string().nullable(),
          notes: Yup.string().nullable(),
        })
      )
      .min(1, "At least one work item is required"),
  })

  const buildInitialValues = () => {
    if (isEdit && editedData) {
      return {
        vehicle_id: editedData.vehicle
          ? { label: buildVehicleOptionLabel(editedData.vehicle), value: editedData.vehicle_id ?? editedData.vehicle?.id }
          : "",
        vehicle_info: buildVehicleInfoFromRaw(editedData.vehicle),
        money_receipt_id: editedData.money_receipt
          ? { label: `${editedData.money_receipt.mr_no || `#${editedData.money_receipt.id}`}`, value: editedData.money_receipt.id }
          : "",
        assigned_date: editedData.assigned_date?.slice(0, 16) || "",
        notes: editedData.notes || "",
        items:
          editedData.items?.map(i => ({
            id: i.id || "",
            service_list_id: i.service_list
              ? { label: i.service_list.name, value: i.service_list.id }
              : "",
            employee_id: i.employee
              ? { label: i.employee.name, value: i.employee.id }
              : "",
            vendor_id: i.vendor
              ? { label: i.vendor.name, value: i.vendor.id }
              : "",
            status: i.status || "pending",
            due_date: i.due_date?.slice(0, 16) || "",
            notes: i.notes || "",
          })) || [{ ...emptyItem }],
      }
    }
    const defaultAssignedDate = nowLocalDatetimeString()
    return {
      vehicle_id: "",
      vehicle_info: emptyVehicleInfo(),
      money_receipt_id: "",
      assigned_date: defaultAssignedDate,
      notes: "",
      items: [{ ...emptyItem, due_date: addOneDay(defaultAssignedDate) }],
    }
  }

  const onSubmit = async (values, { resetForm }) => {
    if (isPreviewOnly) return

    const rawVehicle = values.vehicle_id
    const vehicleId = rawVehicle && typeof rawVehicle === "object" ? rawVehicle.value : rawVehicle
    const payload = {
      vehicle_id: vehicleId,
      money_receipt_id: (values.money_receipt_id?.value ?? values.money_receipt_id) || null,
      assigned_date: values.assigned_date,
      notes: values.notes?.trim() || null,
      vehicle_info: values.vehicle_info,
      items: values.items.map(i => ({
        ...(i.id ? { id: i.id } : {}),
        service_list_id: i.service_list_id?.value ?? i.service_list_id,
        employee_id: i.employee_id?.value ?? i.employee_id,
        vendor_id: i.vendor_id?.value ?? i.vendor_id,
        status: i.status || "pending",
        due_date: i.due_date || null,
        notes: i.notes?.trim() || null,
      })),
    }

    // Editing an already-loaded record, or updating the pending work order
    // that was found for this vehicle — otherwise this is a fresh create.
    const targetId = isEdit
      ? editedData.id
      : matchedWorkOrder?.status === "pending"
        ? matchedWorkOrder.id
        : null

    const action = targetId
      ? editMutation({ id: targetId, ...payload })
      : createMutation(payload)

    try {
      const res = await action
      toast.success(
        res?.message ?? `Work order ${targetId ? "updated" : "created"} successfully.`
      )
      resetForm()
      toggleModal()
      onSuccess?.()
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Failed to save work order."
      toast.error(msg)
    }
  }

  const initialValues = useMemo(() => buildInitialValues(), [isEdit, editedData])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: validation,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // Loads a found work order's fields into the form (autofill for a pending
  // match, preview data for any other status), or clears them back to a
  // fresh blank state when the vehicle has no existing work order.
  const applyWorkOrderDataToForm = workOrder => {
    if (workOrder) {
      formik.setFieldValue(
        "money_receipt_id",
        workOrder.money_receipt
          ? { label: workOrder.money_receipt.mr_no || `#${workOrder.money_receipt.id}`, value: workOrder.money_receipt.id }
          : ""
      )
      formik.setFieldValue("assigned_date", workOrder.assigned_date?.slice(0, 16) || nowLocalDatetimeString())
      formik.setFieldValue("notes", workOrder.notes || "")
      formik.setFieldValue(
        "items",
        workOrder.items?.length
          ? workOrder.items.map(i => ({
              id: i.id || "",
              service_list_id: i.service_list ? { label: i.service_list.name, value: i.service_list.id } : "",
              employee_id: i.employee ? { label: i.employee.name, value: i.employee.id } : "",
              vendor_id: i.vendor ? { label: i.vendor.name, value: i.vendor.id } : "",
              status: i.status || "pending",
              due_date: i.due_date?.slice(0, 16) || "",
              notes: i.notes || "",
            }))
          : [{ ...emptyItem }]
      )
      setOpenIndices(openLastOnly(workOrder.items?.length || 1))
    } else {
      const defaultAssignedDate = nowLocalDatetimeString()
      formik.setFieldValue("money_receipt_id", "")
      formik.setFieldValue("assigned_date", defaultAssignedDate)
      formik.setFieldValue("notes", "")
      formik.setFieldValue("items", [{ ...emptyItem, due_date: addOneDay(defaultAssignedDate) }])
      setOpenIndices(openLastOnly(1))
    }
  }

  const handleVehicleSelect = useCallback(
    vehicle => {
      formik.setFieldValue("vehicle_info", buildVehicleInfoFromRaw(vehicle))
      setShowVehicleDetails(!!vehicle)

      // Only relevant when creating a new work order — editing an existing
      // record already has its own fixed target.
      if (isEdit) return

      if (!vehicle?.id) {
        setMatchedWorkOrder(null)
        applyWorkOrderDataToForm(null)
        return
      }

      setCheckingVehicle(true)
      getWorkOrderByVehicle(vehicle.id)
        .then(res => {
          const found = res?.data ?? null
          setMatchedWorkOrder(found)
          applyWorkOrderDataToForm(found)
        })
        .catch(() => {
          setMatchedWorkOrder(null)
          applyWorkOrderDataToForm(null)
        })
        .finally(() => setCheckingVehicle(false))
    },
    [formik, isEdit]
  )

  // Only one item is expanded by default: the single item if there's just one,
  // otherwise the last (most recently added) item.
  const openLastOnly = count => Array.from({ length: count }, (_, i) => i === count - 1)

  const [openIndices, setOpenIndices] = useState(() => openLastOnly(initialValues.items.length))

  useEffect(() => {
    setOpenIndices(openLastOnly(initialValues.items.length))
  }, [initialValues])

  // Accordion: at most one item open at a time. Clicking the open item closes it;
  // clicking any other item opens it and closes the rest.
  const toggleItem = index =>
    setOpenIndices(prev => prev.map((_, i) => (i === index ? !prev[index] : false)))

  // If the user submits with an invalid item, expand the first one so the error is visible.
  useEffect(() => {
    if (!formik.submitCount) return
    const itemErrors = formik.errors.items
    if (!Array.isArray(itemErrors)) return
    const firstErrorIndex = itemErrors.findIndex(Boolean)
    if (firstErrorIndex === -1) return
    setOpenIndices(prev => prev.map((_, i) => i === firstErrorIndex))
  }, [formik.submitCount])

  // Clean slate each time the modal opens for a fresh create — a stale
  // match from a previous vehicle shouldn't leak into the next session.
  useEffect(() => {
    if (modal && !isEdit) {
      setMatchedWorkOrder(null)
    }
  }, [modal, isEdit])

  // Due date autofill: whenever assigned date changes, sync it into any
  // item whose due date is still empty (one day later, same time). Items
  // the user already set a due date on are left alone.
  const prevAssignedDateRef = useRef(formik.values.assigned_date)
  useEffect(() => {
    if (prevAssignedDateRef.current === formik.values.assigned_date) return
    prevAssignedDateRef.current = formik.values.assigned_date

    const nextDue = addOneDay(formik.values.assigned_date)
    formik.setFieldValue(
      "items",
      formik.values.items.map(item =>
        item.due_date ? item : { ...item, due_date: nextDue }
      )
    )
  }, [formik.values.assigned_date])

  const addItem = () => {
    const updated = [
      ...formik.values.items,
      { ...emptyItem, due_date: addOneDay(formik.values.assigned_date) },
    ]
    formik.setFieldValue("items", updated)
    setOpenIndices(openLastOnly(updated.length))
  }

  const removeItem = index => {
    const updated = formik.values.items.filter((_, i) => i !== index)
    formik.setFieldValue("items", updated.length ? updated : [{ ...emptyItem }])
    setOpenIndices(prev => {
      if (!updated.length || updated.length === 1) return openLastOnly(updated.length || 1)
      return prev.filter((_, i) => i !== index)
    })
  }

  const [removeIndex, setRemoveIndex] = useState(null)
  const requestRemoveItem = index => setRemoveIndex(index)
  const cancelRemoveItem = () => setRemoveIndex(null)
  const confirmRemoveItem = () => {
    removeItem(removeIndex)
    setRemoveIndex(null)
  }

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Work Order" : isPreviewOnly ? "Work Order (View Only)" : "Add Work Order"}
      size="xl"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <VehicleIdSelect formik={formik} onVehicleSelect={handleVehicleSelect} />
        </div>

        {checkingVehicle && (
          <Alert color="light" className="d-flex align-items-center gap-2 py-2">
            <Spinner size="sm" /> Checking for an existing work order on this vehicle...
          </Alert>
        )}

        {!checkingVehicle && matchedWorkOrder && (
          <Alert color={isPreviewOnly ? "warning" : "info"} className="py-2">
            {isPreviewOnly
              ? `This vehicle already has work order ${matchedWorkOrder.work_order_no} (${matchedWorkOrder.status.replace("_", " ")}). Showing details — view only.`
              : `Pending work order ${matchedWorkOrder.work_order_no} found for this vehicle — loaded for editing.`}
          </Alert>
        )}

        {showVehicleDetails && (
          <div className="mb-3">
            <Button
              color="link"
              size="sm"
              type="button"
              className="p-0 mb-2"
              onClick={() => setShowVehicleDetails(v => !v)}
            >
              {showVehicleDetails ? "Hide" : "Edit"} vehicle details
            </Button>
            <div className="card mb-0">
              <div className="card-header bg-light">
                <h6 className="mb-0">Vehicle Details</h6>
              </div>
              <div className="card-body">
                <Row className="g-2">
                  <Col md={4}>
                    <Label>Chassis No.</Label>
                    <InputField
                      name="vehicle_info.chassis_no"
                      value={formik.values.vehicle_info?.chassis_no || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Engine No.</Label>
                    <InputField
                      name="vehicle_info.engine_no"
                      value={formik.values.vehicle_info?.engine_no || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Brand</Label>
                    <InputField
                      name="vehicle_info.brand"
                      value={formik.values.vehicle_info?.brand || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Model</Label>
                    <InputField
                      name="vehicle_info.model"
                      value={formik.values.vehicle_info?.model || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Year</Label>
                    <InputField
                      name="vehicle_info.model_year"
                      value={formik.values.vehicle_info?.model_year || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Color</Label>
                    <InputField
                      name="vehicle_info.color"
                      value={formik.values.vehicle_info?.color || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Engine CC</Label>
                    <InputField
                      name="vehicle_info.cc"
                      value={formik.values.vehicle_info?.cc || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Fuel Type</Label>
                    <InputField
                      name="vehicle_info.fuel_type"
                      value={formik.values.vehicle_info?.fuel_type || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Tyre Size</Label>
                    <InputField
                      name="vehicle_info.tyre_size"
                      value={formik.values.vehicle_info?.tyre_size || ""}
                      onChange={formik.handleChange}
                    />
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        )}

        <Row className="g-2 mb-3">
          <Col md={4}>
            <SearchableSelect
              fetchFn={getMoneyReceipts}
              formik={formik}
              name="money_receipt_id"
              label="Money Receipt (filtered by vehicle)"
              placeholder="Type to search..."
              getOptionLabel={item =>
                `${item.mr_no || `#${item.id}`}${item.customer?.name ? ` - ${item.customer.name}` : ""}`
              }
              extraParams={formik.values.vehicle_id ? { vehicle_id: formik.values.vehicle_id?.value ?? formik.values.vehicle_id } : undefined}
              disabled={isPreviewOnly}
            />
          </Col>
          <Col md={4}>
            <Label>Status <small className="text-muted">(auto from items)</small></Label>
            <div>
              <Badge
                color={STATUS_BADGE[computeStatusFromItems(formik.values.items)] || "secondary"}
                className="fs-6 px-3 py-2"
              >
                {STATUS_OPTIONS.find(s => s.value === computeStatusFromItems(formik.values.items))?.label || "Pending"}
              </Badge>
            </div>
          </Col>
        </Row>

        <Row className="g-2 mb-3">
          <Col md={4}>
            <Label for="assigned_date">Assigned Date</Label>
            <InputField
              type="datetime-local"
              name="assigned_date"
              id="assigned_date"
              value={formik.values.assigned_date}
              onChange={formik.handleChange}
              disabled={isPreviewOnly}
              invalid={
                formik.touched.assigned_date && !!formik.errors.assigned_date
              }
            />
            {formik.touched.assigned_date && formik.errors.assigned_date && (
              <FormFeedback type="invalid">
                {formik.errors.assigned_date}
              </FormFeedback>
            )}
          </Col>
        </Row>

        <div className="mb-3">
          <Label for="notes">Notes</Label>
          <InputField
            type="textarea"
            name="notes"
            id="notes"
            rows="3"
            value={formik.values.notes}
            onChange={formik.handleChange}
            disabled={isPreviewOnly}
          />
        </div>

        <div className="mb-3">
          <div className="mb-2">
            <Label className="mb-0">
              Work Items <Badge>{formik.values.items.length}</Badge>
            </Label>
          </div>

          {typeof formik.errors.items === "string" && (
            <div className="text-danger small mb-2">
              {formik.errors.items}
            </div>
          )}

          {formik.values.items.map((item, index) => {
            const open = !!openIndices[index]
            const summary = buildItemSummary(item)
            const hasError =
              !open &&
              formik.errors.items?.[index] &&
              typeof formik.errors.items[index] === "object"

            return (
              <div
                key={index}
                className={`border rounded mb-2 ${hasError ? "border-danger" : ""}`}
              >
                <div
                  className="d-flex justify-content-between align-items-center px-3 py-2 bg-light"
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleItem(index)}
                >
                  <div className="d-flex align-items-center gap-2 flex-grow-1 me-2">
                    <Badge color="secondary" pill>{index + 1}</Badge>
                    <span className="fw-medium text-truncate">
                      {summary || "New Work Item"}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    {!open && (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        onClick={e => {
                          e.stopPropagation()
                          toggleItem(index)
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    {!isPreviewOnly && (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 text-danger"
                        onClick={e => {
                          e.stopPropagation()
                          requestRemoveItem(index)
                        }}
                      >
                        <i className="mdi mdi-close" />
                      </Button>
                    )}
                    <i className={`mdi ${open ? "mdi-chevron-up" : "mdi-chevron-down"}`} />
                  </div>
                </div>

                {open && (
                  <div className="p-3">
                    <Row className="g-2 align-items-start mb-2">
                      <Col md={3}>
                        <SearchableSelect
                          fetchFn={getServiceLists}
                          formik={formik}
                          name={`items.${index}.service_list_id`}
                          label="Service"
                          placeholder="Type to search service..."
                          required
                          disabled={isPreviewOnly}
                        />
                      </Col>
                      <Col md={2}>
                        <SearchableSelect
                          fetchFn={getEmployees}
                          formik={formik}
                          name={`items.${index}.employee_id`}
                          label="Employee"
                          placeholder="Type to search employee..."
                          required
                          disabled={isPreviewOnly}
                        />
                      </Col>
                      <Col md={2}>
                        <SearchableSelect
                          fetchFn={getAccountVendors}
                          formik={formik}
                          name={`items.${index}.vendor_id`}
                          label="Vendor/Work Shop/Garage/Shop"
                          placeholder="Type to search vendor..."
                          required
                          disabled={isPreviewOnly}
                        />
                      </Col>
                      <Col md={2}>
                        <Label for={`items.${index}.status`}>Status <span className="text-danger">*</span></Label>
                        <InputField
                          type="select"
                          name={`items.${index}.status`}
                          id={`items.${index}.status`}
                          value={item.status}
                          onChange={formik.handleChange}
                          disabled={isPreviewOnly}
                          invalid={
                            formik.touched.items?.[index]?.status && !!formik.errors.items?.[index]?.status
                          }
                        >
                          {STATUS_OPTIONS.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </InputField>
                        {formik.touched.items?.[index]?.status && formik.errors.items?.[index]?.status && (
                          <FormFeedback type="invalid">
                            {formik.errors.items[index].status}
                          </FormFeedback>
                        )}
                      </Col>
                      <Col md={2}>
                        <Label for={`items.${index}.due_date`}>Due Date</Label>
                        <InputField
                          type="datetime-local"
                          name={`items.${index}.due_date`}
                          id={`items.${index}.due_date`}
                          value={item.due_date}
                          onChange={formik.handleChange}
                          disabled={isPreviewOnly}
                        />
                      </Col>
                    </Row>
                    <Row className="g-2 align-items-start">
                      <Col md={12}>
                        <Label for={`items.${index}.notes`}>Notes</Label>
                        <InputField
                          type="textarea"
                          name={`items.${index}.notes`}
                          id={`items.${index}.notes`}
                          rows="2"
                          value={item.notes}
                          onChange={formik.handleChange}
                          placeholder="Optional"
                          disabled={isPreviewOnly}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            )
          })}

          {!isPreviewOnly && (
            <div className="text-end">
              <Button outline size="sm" color="primary" type="button" onClick={addItem}>
                + Add Item
              </Button>
            </div>
          )}
        </div>

        {!isPreviewOnly && (
          <div className="text-end">
            <Button
              color="success"
              type="submit"
              disabled={createLoading || editLoading}
            >
              {isEdit || matchedWorkOrder?.status === "pending" ? "Save Changes" : "Create Work Order"}
              {createLoading || editLoading ? "..." : ""}
            </Button>
          </div>
        )}
      </Form>

      <ConfirmModal
        isOpen={removeIndex !== null}
        toggle={cancelRemoveItem}
        title="Remove Work Item"
        message="Are you sure you want to remove this work item?"
        confirmLabel="Remove"
        btnColor="danger"
        onConfirm={confirmRemoveItem}
      />
    </ThemeModal>
  )
}

export default WorkOrderAddEdit
